import type { TeamMember } from './types';
import type { Player } from '@api-service/players';

// Slots 0-10 are the starting XI; 11-14 are the bench.
export const XI_SLOT_MAX = 10;

const XI_ROW_ORDER = ['GK', 'DEF', 'MID', 'FWD'] as const;

type PitchXiMember = {
  playerId: number;
  player: { position: string } | null;
};

export type PitchXiRow<T> = {
  key: string;
  members: T[];
  className: string;
};

const xiRowClassName = (position: string, memberCount: number): string => {
  if (memberCount >= 4) return 'justify-between';
  if (position === 'FWD') return 'justify-around w-full md:w-96 mx-auto';
  return 'justify-around w-full md:w-80 mx-auto';
};

/**
 * Groups the starting XI (slots 0-10) into pitch rows by each player's actual
 * position, so whatever formation the rating server returns renders on the
 * right lines (it can pick any legal formation, not just the builder's fixed
 * shape). Returns null when any XI slot is empty — the position is unknowable,
 * so callers fall back to the fixed builder layout.
 */
export const buildXiRowsByPosition = <T extends PitchXiMember>(
  team: T[]
): Array<PitchXiRow<T>> | null => {
  const startingXi = team.filter(({ playerId }) => playerId <= XI_SLOT_MAX);
  if (startingXi.some(({ player }) => !player)) return null;
  return XI_ROW_ORDER.map((position) => {
    const members = startingXi.filter(
      ({ player }) => player?.position === position
    );
    return {
      key: position,
      members,
      className: xiRowClassName(position, members.length)
    };
  });
};

export const getTeamTotalValue = (team: TeamMember[]): number =>
  team.reduce((total, { player }) => {
    if (player?.price) {
      return total + player.price;
    }
    return total;
  }, 0);

const POSITION_TO_ID: Record<string, number> = {
  GK: 1,
  DEF: 2,
  MID: 3,
  FWD: 4
};

export const mapPlayerToLegacySquadEntry = (player: Player) => ({
  code: player.externalIds.fplCode,
  fpl: {
    id: player.externalIds.fplId,
    web_name: player.displayName,
    now_cost: Math.round(player.price * 10)
  },
  data: {
    positionId: POSITION_TO_ID[player.position] ?? 0,
    predictions: player.fixtures.map((f) => ({
      predicted_pts: f.predictions.points
    }))
  },
  team: {
    codeName: player.team.shortName,
    name: player.team.fullName
  }
});

export const isBelowTeamLimit = (
  player: Player,
  selectedPlayers: Player[],
  maxCount: number
): boolean => {
  const count = selectedPlayers.filter(
    (p) => p.team.shortName === player.team.shortName
  ).length;
  return count < maxCount;
};
